import {
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from '@tanstack/react-query'
import axios from 'axios'
import { z } from 'zod'

export const LoxoKpiRowSchema = z.object({
  user_id: z.number(),
  name: z.string(),
  period: z.string(),
  kpi: z.string(),
  value: z.number(),
})

export type LoxoKpiRow = z.infer<typeof LoxoKpiRowSchema>

const getLoxoKpis = async (params: { start: string; end: string }) => {
  const response = await axios.get('/api/loxo/kpis', { params })
  return z.array(LoxoKpiRowSchema).parse(response.data)
}

export const useLoxoKpis = (params: { start: string; end: string }) => {
  return useSuspenseQuery({
    queryKey: ['loxoKpis', params],
    queryFn: () => getLoxoKpis(params),
  })
}

export const LoxoScoringConfigSchema = z.object({
  metric_id: z.number(),
  key: z.string(),
  name: z.string(),
  is_percent_metric: z.boolean(),
  month_target: z.number().nullable(),
  month_percent_values: z.array(z.number()),
  month_score_values: z.array(z.number()),
})

export type LoxoScoringConfig = z.infer<typeof LoxoScoringConfigSchema>

const getLoxoScoringConfig = async () => {
  const response = await axios.get('/api/loxo/scoring-config')
  return z.array(LoxoScoringConfigSchema).parse(response.data)
}

export const useLoxoScoringConfig = () => {
  return useSuspenseQuery({
    queryKey: ['loxoScoringConfig'],
    queryFn: getLoxoScoringConfig,
  })
}

export type LoxoScoringConfigUpdate = {
  metric_id: number
  month_target: number | null
  month_percent_values: number[]
  month_score_values: number[]
}

export const useUpdateLoxoScoringConfig = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: LoxoScoringConfigUpdate[]) =>
      axios.patch('/api/loxo/scoring-config', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['loxoScoringConfig'] })
    },
  })
}
